// Tabla «Promos de referido vigentes» del paso Referidos: qué gana quien recomienda y qué gana su amigo.
// Sin promos vigentes, o si la lectura falla, no se pinta: el paso nunca se detiene por esto.
import { Users } from 'lucide-react';
import {
    referralRewardChipParts,
    referralRewardText,
} from '@/domains/retail/admin/promotions/lib/referralPromotionText';
import { triggerShortLabel } from '@/domains/retail/admin/promotions/lib/referralPromotionOptions';
import { PromoBanner, type PromoBannerRow } from '@/domains/sales/wizard/components/PromoBanner';
import { formatDateMx } from '@/lib/dateHelpers';
import { useLiveReferralPromotions } from '../api/useWizardPromotions';
import type { LiveReferralPromotionItem } from '../api/wizardPromotionsContract';
import {
    LIVE_REFERRAL_PROMOTIONS_LABEL as LABEL,
    REFERRAL_CHANNEL_LABEL as CHANNEL_LABEL,
} from '../constants';

function validityText(validUntil: string | null): string {
    return validUntil ? `hasta el ${formatDateMx(validUntil)}` : 'Sin fecha de fin';
}

function toBannerRow(promo: LiveReferralPromotionItem): PromoBannerRow {
    const detail = promo.customerText.trim();
    const referred = promo.referredReward ? referralRewardChipParts(promo.referredReward) : null;
    return {
        key: promo.promotionId,
        name: promo.name,
        ...(detail !== '' && detail !== promo.name && { detail }),
        badge: CHANNEL_LABEL[promo.channel],
        note: `${triggerShortLabel(promo)} · ${validityText(promo.validUntil)}`,
        reach: referralRewardText(promo.referrerReward),
        chips: referred ? [{ key: 'referred', label: referred.label, tone: 'credit' }] : [],
        ...(referred === null && { chipsDetail: referralRewardText(null) }),
        ...(referred?.detail && { chipsDetail: referred.detail }),
    };
}

export function ReferralPromotionsBanner({
    sessionId,
    opening,
    customerName,
}: {
    sessionId: string;
    /** Apertura sin venta: solo las que se ganan al abrir cuenta. */
    opening: boolean;
    customerName: string;
}) {
    const { data } = useLiveReferralPromotions(sessionId, opening);
    const items = [...(data?.items ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
    if (items.length === 0) return null;
    return (
        <PromoBanner
            tone="sale"
            label={LABEL}
            icon={Users}
            summary={`Cuéntaselas a ${customerName} antes de capturar`}
            columns={['Promo', 'Tú ganas', 'Tu amigo gana']}
            rows={items.map(toBannerRow)}
            footer="El premio llega cuando el amigo cumple la condición, no al terminar este paso."
        />
    );
}
